import { useEffect, useRef, useState, type KeyboardEvent } from 'react'

export const localDateTime = (value: Date) => {
  const pad = (number: number) => String(number).padStart(2, '0')
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(value.getMinutes())}:${pad(value.getSeconds())}`
}

type Props = { label: string; value: string; onChange: (value: string) => void; onClose: () => void }
const pad = (value: number) => String(value).padStart(2, '0')

export function TransmissionDateTimePicker({ label, value, onChange, onClose }: Props) {
  const initial = new Date(value)
  const valid = Number.isFinite(initial.getTime()) ? initial : new Date()
  const [day, setDay] = useState(new Date(valid.getFullYear(), valid.getMonth(), valid.getDate()))
  const [month, setMonth] = useState(new Date(valid.getFullYear(), valid.getMonth(), 1))
  const [time, setTime] = useState([pad(valid.getHours()), pad(valid.getMinutes()), pad(valid.getSeconds())])
  const [error, setError] = useState('')
  const calendarRef = useRef<HTMLDivElement>(null)
  const today = new Date(), lastDay = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const leading = (month.getDay() + 6) % 7
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
  const selectedInMonth = day.getFullYear() === month.getFullYear() && day.getMonth() === month.getMonth()
  const title = month.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null
    calendarRef.current?.querySelector<HTMLButtonElement>('button[tabindex="0"]')?.focus()
    return () => { if (previous?.isConnected) previous.focus() }
  }, [])

  function shiftMonth(delta: number) { setMonth(new Date(month.getFullYear(), month.getMonth() + delta, 1)) }
  function choose(date: Date) { setDay(date); setError('') }
  function moveDay(event: KeyboardEvent<HTMLButtonElement>, date: Date) {
    const delta = ({ ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 } as Record<string, number>)[event.key]
    if (delta === undefined) return
    event.preventDefault()
    const next = new Date(date.getFullYear(), date.getMonth(), date.getDate() + delta)
    if (next > lastDay) return
    choose(next); setMonth(new Date(next.getFullYear(), next.getMonth(), 1))
    requestAnimationFrame(() => calendarRef.current?.querySelector<HTMLButtonElement>(`[data-day="${next.getDate()}"]`)?.focus())
  }
  function changeTime(index: number, value: string) {
    if (!/^\d{0,2}$/.test(value)) return
    setTime(current => current.map((item, position) => position === index ? value : item)); setError('')
  }
  function stepTime(index: number, delta: number) {
    const maximum = index === 0 ? 24 : 60
    const next = (Number(time[index]) + delta + maximum) % maximum
    changeTime(index, pad(next))
  }
  function apply() {
    const values = time.map(Number)
    if (time.some(item => item === '') || values.some((item, index) => item < 0 || item > (index === 0 ? 23 : 59))) {
      setError('Informe horas de 00 a 23 e minutos e segundos de 00 a 59.'); return
    }
    const next = new Date(day.getFullYear(), day.getMonth(), day.getDate(), values[0], values[1], values[2])
    if (next.getTime() > Date.now()) { setError('Escolha uma data e um horário que já passaram.'); return }
    onChange(localDateTime(next)); onClose()
  }

  return <section className="transmission-datetime-picker" role="group" aria-label={`${label}: data e hora`} onKeyDown={event => {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); onClose() }
    if (event.key === 'Enter' && event.target instanceof HTMLInputElement) { event.preventDefault(); apply() }
  }}>
    <div className="transmission-picker-heading"><div><small>DATA E HORÁRIO</small><strong>{label}</strong></div><button type="button" aria-label="Fechar calendário" onClick={onClose}>×</button></div>
    <div className="transmission-picker-calendar">
      <div className="transmission-picker-month"><button type="button" aria-label="Mês anterior" onClick={() => shiftMonth(-1)}>‹</button><strong aria-live="polite">{title}</strong><button type="button" aria-label="Próximo mês" disabled={month.getFullYear() === today.getFullYear() && month.getMonth() === today.getMonth()} onClick={() => shiftMonth(1)}>›</button></div>
      <div className="transmission-picker-days" ref={calendarRef}>
        {['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB', 'DOM'].map(name => <small key={name}>{name}</small>)}
        {Array.from({ length: leading }, (_, index) => <span key={`empty-${index}`} />)}
        {Array.from({ length: days }, (_, index) => {
          const number = index + 1, date = new Date(month.getFullYear(), month.getMonth(), number)
          const selected = selectedInMonth && day.getDate() === number
          return <button key={number} type="button" data-day={number} aria-label={date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })} aria-pressed={selected} aria-current={date.getTime() === lastDay.getTime() ? 'date' : undefined} disabled={date > lastDay} tabIndex={selected || !selectedInMonth && number === 1 ? 0 : -1} onClick={() => choose(date)} onKeyDown={event => moveDay(event, date)}>{number}</button>
        })}
      </div>
    </div>
    <div className="transmission-picker-time" role="group" aria-label="Horário com segundos">
      {['Horas', 'Minutos', 'Segundos'].map((name, index) => <label key={name}><span>{name}</span><div><button type="button" aria-label={`Aumentar ${name.toLowerCase()}`} onClick={() => stepTime(index, 1)}>+</button><input aria-label={name} type="text" inputMode="numeric" maxLength={2} value={time[index]} onFocus={event => event.currentTarget.select()} onChange={event => changeTime(index, event.target.value)} onBlur={() => { if (time[index] !== '') changeTime(index, pad(Number(time[index]))) }}/><button type="button" aria-label={`Diminuir ${name.toLowerCase()}`} onClick={() => stepTime(index, -1)}>−</button></div></label>)}
    </div>
    {error && <p className="transmission-export-error" role="alert">{error}</p>}
    <div className="transmission-picker-actions"><button type="button" className="transmission-export-secondary" onClick={onClose}>Voltar ao período</button><button type="button" className="transmission-export-primary" onClick={apply}>Confirmar horário</button></div>
  </section>
}
